import { Navigate, Route } from 'react-router-dom';
import { RouteBoundary } from './components/RouteBoundary';
import { AppShell } from './layout/AppShell';
import { RequireAuth } from './lib/auth';
import { lazyImport } from './lib/lazy';
import { SentryRoutes } from './lib/sentry';
import { LegalRedirectPage } from './pages/LegalRedirectPage';
import { LoginPage } from './pages/LoginPage';
import { SettingsLayout } from './pages/settings/SettingsLayout';

// Route boundaries keep the shell/navigation visible while feature code loads.
// Keep feature implementations and resource configs out of the startup graph.
const AccountDeletionRequestPage = lazyImport(() =>
  import('./pages/AccountDeletionRequestPage').then((m) => ({ default: m.AccountDeletionRequestPage })),
);
const DriversPage = lazyImport(() =>
  import('./pages/drivers/DriversPage').then((m) => ({ default: m.DriversPage })),
);
const PassengersPage = lazyImport(() =>
  import('./pages/passengers/PassengersPage').then((m) => ({ default: m.PassengersPage })),
);
const AgentLoginCodesPage = lazyImport(() =>
  import('./pages/settings/AgentLoginCodesPage').then((m) => ({ default: m.AgentLoginCodesPage })),
);
const BookingAmendmentPolicyPage = lazyImport(() =>
  import('./pages/settings/BookingAmendmentPolicyPage').then((m) => ({
    default: m.BookingAmendmentPolicyPage,
  })),
);
const DeletedAgentsPage = lazyImport(() =>
  import('./pages/settings/DeletedAgentsPage').then((m) => ({ default: m.DeletedAgentsPage })),
);
const DeletedPassengersPage = lazyImport(() =>
  import('./pages/settings/DeletedPassengersPage').then((m) => ({ default: m.DeletedPassengersPage })),
);
const DeletionRequestsPage = lazyImport(() =>
  import('./pages/settings/DeletionRequestsPage').then((m) => ({ default: m.DeletionRequestsPage })),
);
const DiagnosticsPage = lazyImport(() =>
  import('./pages/settings/DiagnosticsPage').then((m) => ({ default: m.DiagnosticsPage })),
);
const DispatchPolicyPage = lazyImport(() =>
  import('./pages/settings/DispatchPolicyPage').then((m) => ({ default: m.DispatchPolicyPage })),
);
const DriverApplicationPolicyPage = lazyImport(() =>
  import('./pages/settings/DriverApplicationPolicyPage').then((m) => ({
    default: m.DriverApplicationPolicyPage,
  })),
);
const DriverDocumentPolicyPage = lazyImport(() =>
  import('./pages/settings/DriverDocumentPolicyPage').then((m) => ({ default: m.DriverDocumentPolicyPage })),
);
const OrgPage = lazyImport(() => import('./pages/settings/OrgPage').then((m) => ({ default: m.OrgPage })));
const OrgUsersPage = lazyImport(() =>
  import('./pages/settings/OrgUsersPage').then((m) => ({ default: m.OrgUsersPage })),
);
const PartnerConnectionsPage = lazyImport(() =>
  import('./pages/settings/PartnerConnectionsPage').then((m) => ({ default: m.PartnerConnectionsPage })),
);
const PartnerRequestEvidenceRoute = lazyImport(() =>
  import('./pages/settings/PartnerRequestEvidence').then((m) => ({ default: m.PartnerRequestEvidenceRoute })),
);
const PermissionsPage = lazyImport(() =>
  import('./pages/settings/PermissionsPage').then((m) => ({ default: m.PermissionsPage })),
);
const ProviderOnboardingPage = lazyImport(() =>
  import('./pages/settings/ProviderOnboardingPage').then((m) => ({ default: m.ProviderOnboardingPage })),
);
const ServiceAreasPage = lazyImport(() =>
  import('./pages/settings/ServiceAreasPage').then((m) => ({ default: m.ServiceAreasPage })),
);
const SystemSettingsPage = lazyImport(() =>
  import('./pages/settings/SystemSettingsPage').then((m) => ({ default: m.SystemSettingsPage })),
);
const TrackingPage = lazyImport(() =>
  import('./pages/tracking/TrackingPage').then((m) => ({ default: m.TrackingPage })),
);
const TripsPage = lazyImport(() => import('./pages/trips/TripsPage').then((m) => ({ default: m.TripsPage })));
const ServiceGroupsPage = lazyImport(() =>
  import('./pages/settings/ResourcePages').then((m) => ({ default: m.ServiceGroupsPage })),
);
const VehiclesPage = lazyImport(() =>
  import('./pages/settings/ResourcePages').then((m) => ({ default: m.VehiclesPage })),
);
const PassengerGroupsPage = lazyImport(() =>
  import('./pages/settings/ResourcePages').then((m) => ({ default: m.PassengerGroupsPage })),
);
const EquipmentPage = lazyImport(() =>
  import('./pages/settings/ResourcePages').then((m) => ({ default: m.EquipmentPage })),
);
const ReasonsPage = lazyImport(() =>
  import('./pages/settings/ReasonSettingsPage').then((m) => ({
    default: () => <m.ReasonSettingsPage variant="reason" />,
  })),
);
const RatingReasonsPage = lazyImport(() =>
  import('./pages/settings/ReasonSettingsPage').then((m) => ({
    default: () => <m.ReasonSettingsPage variant="rating" />,
  })),
);

const RealtimeDashboardPage = lazyImport(() =>
  import('./pages/dashboard/RealtimeDashboardPage').then((m) => ({ default: m.RealtimeDashboardPage })),
);
const AnalyticsPage = lazyImport(() =>
  import('./pages/dashboard/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })),
);
// Lazy for a different reason: the route below is registered only outside
// production, so the dev/QA tool's code must not ship in the prod bundle.
const DispatchSimulationPage = lazyImport(() =>
  import('./pages/dispatch/DispatchSimulationPage').then((m) => ({ default: m.DispatchSimulationPage })),
);
const ReportsHubPage = lazyImport(() =>
  import('./pages/reports/ReportsHubPage').then((m) => ({ default: m.ReportsHubPage })),
);
const ReportsHubPrototypePage = import.meta.env.DEV
  ? lazyImport(() =>
      import('./pages/reports/ReportsHubPrototypePage').then((m) => ({ default: m.ReportsHubPrototypePage })),
    )
  : null;

export function App() {
  return (
    <SentryRoutes>
      <Route path="/login" element={<LoginPage />} />
      {/* Public legal pages — the mobile apps link ${appBaseUrl}/legal/* here;
          the backend-configured URL (System Settings) decides where they go. */}
      <Route path="/legal/terms" element={<LegalRedirectPage kind="terms" />} />
      <Route path="/legal/privacy" element={<LegalRedirectPage kind="privacy" />} />
      {/* Public deletion-request form — the mobile apps' "ขอลบบัญชี" row opens
          ${appBaseUrl}/account-deletion-request; also the Play Data-safety URL. */}
      <Route
        path="/account-deletion-request"
        element={
          <RouteBoundary>
            <AccountDeletionRequestPage />
          </RouteBoundary>
        }
      />
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* Dashboard menu (left rail) lands on the realtime dashboard; the
            ChartPie/analytics icon points at /analytics. */}
        <Route index element={<RealtimeDashboardPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="trips" element={<TripsPage />} />
        <Route path="trips/:tripId" element={<TripsPage />} />
        <Route path="tracking" element={<TrackingPage />} />
        <Route path="passengers" element={<PassengersPage />} />
        <Route path="passengers/:passengerId" element={<PassengersPage />} />
        <Route path="drivers" element={<DriversPage />} />
        <Route path="drivers/:driverId" element={<DriversPage />} />
        {/* dev/QA tool — never registered in production; the catch-all below redirects there */}
        {import.meta.env.MODE !== 'production' && (
          <Route path="dispatch-sim" element={<DispatchSimulationPage />} />
        )}
        <Route path="reports/:reportKey?" element={<ReportsHubPage />} />
        {ReportsHubPrototypePage && (
          <Route path="reports-review/:reportKey?" element={<ReportsHubPrototypePage />} />
        )}
        <Route path="settings" element={<SettingsLayout />}>
          <Route index element={<Navigate to="org" replace />} />
          <Route path="org" element={<OrgPage />} />
          <Route path="provider-onboarding" element={<ProviderOnboardingPage />} />
          <Route path="agent-login-codes" element={<AgentLoginCodesPage />} />
          <Route path="partner-connections" element={<PartnerConnectionsPage />}>
            <Route path=":connectionId/request-evidence" element={<PartnerRequestEvidenceRoute />} />
          </Route>
          <Route path="service-groups" element={<ServiceGroupsPage />} />
          <Route path="vehicles" element={<VehiclesPage />} />
          <Route path="org-users" element={<OrgUsersPage />} />
          <Route path="passenger-groups" element={<PassengerGroupsPage />} />
          <Route path="service-areas" element={<ServiceAreasPage />} />
          <Route path="dispatch-policy" element={<DispatchPolicyPage />} />
          <Route path="booking-amendments" element={<BookingAmendmentPolicyPage />} />
          <Route path="driver-document-policy" element={<DriverDocumentPolicyPage />} />
          <Route path="driver-application-policy" element={<DriverApplicationPolicyPage />} />
          <Route path="equipment" element={<EquipmentPage />} />
          <Route path="reasons" element={<ReasonsPage />} />
          <Route path="rating-reasons" element={<RatingReasonsPage />} />
          <Route path="permissions" element={<PermissionsPage />} />
          <Route path="system" element={<SystemSettingsPage />} />
          <Route path="diagnostics" element={<DiagnosticsPage />} />
          <Route path="deletion-requests" element={<DeletionRequestsPage />} />
          <Route path="deleted-agents" element={<DeletedAgentsPage />} />
          <Route path="deleted-passengers" element={<DeletedPassengersPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </SentryRoutes>
  );
}
